import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Checkbox, Modal, Select, Spin, Tag, message } from 'antd';
import AdaptiveTable from './AdaptiveTable';
import {
  shipmentApi,
  type AgentCargoItemDTO,
  type CargoBatchAssignment,
  type CartonBatchDTO,
  type MatchAgentCargoResultDTO,
} from '../api/kfc';

const NONE = '__none__';

/** 与后端 autoAssignToBatches 一致：依次填满各批次剩余件数，溢出归最后一批；已生成的箱跳过 */
export function autoAssignToBatches(
  items: AgentCargoItemDTO[],
  batches: CartonBatchDTO[],
): Record<number, string> {
  const out: Record<number, string> = {};
  if (!items.length || !batches.length) return out;
  const remaining = batches.map((b) => Math.max(0, (b.pieceCount ?? 0) - (b.materializedCount ?? 0)));
  let cursor = 0;
  items.forEach((item, i) => {
    if (item.existingCartonId) return;
    while (cursor < batches.length - 1 && remaining[cursor] <= 0) cursor++;
    remaining[cursor]--;
    const batchId = batches[cursor].batchId;
    if (batchId) out[item.sourceIndex ?? i] = batchId;
  });
  return out;
}

export interface AgentCargoBatchMatchModalProps {
  open: boolean;
  shipmentId?: string;
  /** 已有的代理箱清单与绑定批次（如同步结果）；缺省时打开后从后端预览加载 */
  initialItems?: AgentCargoItemDTO[];
  initialBatches?: CartonBatchDTO[];
  onClose: () => void;
  onDone: (result: MatchAgentCargoResultDTO) => void;
}

/** 将代理入仓箱匹配到本运单已绑定的货物批次，并生成单箱 */
const AgentCargoBatchMatchModal: React.FC<AgentCargoBatchMatchModalProps> = ({
  open,
  shipmentId,
  initialItems,
  initialBatches,
  onClose,
  onDone,
}) => {
  const [items, setItems] = useState<AgentCargoItemDTO[]>([]);
  const [batches, setBatches] = useState<CartonBatchDTO[]>([]);
  const [assign, setAssign] = useState<Record<number, string>>({});
  const [updateCount, setUpdateCount] = useState(true);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !shipmentId) return;
    setUpdateCount(true);
    const apply = (its: AgentCargoItemDTO[], bs: CartonBatchDTO[]) => {
      setItems(its);
      setBatches(bs);
      setAssign(autoAssignToBatches(its, bs));
    };
    if (initialItems?.length && initialBatches?.length) {
      apply(initialItems, initialBatches);
      return;
    }
    setLoading(true);
    shipmentApi
      .previewAgentCargoMatch(shipmentId)
      .then((res) => apply(res.data?.cargoItems || [], res.data?.boundBatches || []))
      .catch((e) => {
        message.error(e.response?.data?.message || '加载代理箱清单失败');
        apply([], []);
      })
      .finally(() => setLoading(false));
  }, [open, shipmentId, initialItems, initialBatches]);

  const keyOf = (r: AgentCargoItemDTO, i: number) => r.sourceIndex ?? i;

  const assignedCount = useMemo(() => {
    const counts: Record<string, number> = {};
    Object.values(assign).forEach((b) => {
      counts[b] = (counts[b] || 0) + 1;
    });
    return counts;
  }, [assign]);

  const batchOptions = [
    { value: NONE, label: '不生成' },
    ...batches.map((b) => ({ value: b.batchId || '', label: `${b.batchId}（${b.pieceCount ?? 0} 件）` })),
  ];

  const submit = () => {
    if (!shipmentId) return;
    const assignments: CargoBatchAssignment[] = Object.entries(assign)
      .filter(([, b]) => b && b !== NONE)
      .map(([idx, b]) => ({ sourceIndex: Number(idx), batchId: b }));
    setSubmitting(true);
    shipmentApi
      .matchAgentCargo(shipmentId, { assignments, updateBatchPieceCount: updateCount })
      .then((res) => {
        const r = res.data || {};
        const created = r.createdCartons?.length ?? 0;
        const updated = r.updatedCartons?.length ?? 0;
        message.success(r.message || `已生成 ${created} 箱，更新 ${updated} 箱`);
        if (r.warnings?.length) {
          Modal.warning({
            title: '匹配提示',
            content: (
              <ul style={{ paddingLeft: 20, margin: 0 }}>
                {r.warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            ),
          });
        }
        onDone(r);
      })
      .catch((e) => message.error(e.response?.data?.message || '匹配失败'))
      .finally(() => setSubmitting(false));
  };

  return (
    <Modal
      title="匹配代理箱到货物批次"
      open={open}
      onCancel={onClose}
      onOk={submit}
      okText="确认匹配"
      confirmLoading={submitting}
      width={900}
      destroyOnClose
      zIndex={1100}
    >
      <Spin spinning={loading}>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="为每个代理入仓箱选择归属批次，确认后按批次生成单箱（状态：抵达代理仓库）。已生成的箱仅更新测量数据。"
        />
        {batches.length > 0 && (
          <div style={{ marginBottom: 12, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {batches.map((b) => {
              const planned = (b.materializedCount ?? 0) + (assignedCount[b.batchId || ''] || 0);
              const mismatch = planned !== (b.pieceCount ?? 0);
              return (
                <Tag key={b.id || b.batchId} color={mismatch ? 'orange' : 'green'}>
                  {b.batchId}：登记 {b.pieceCount ?? 0} 件 / 匹配后 {planned} 箱
                </Tag>
              );
            })}
          </div>
        )}
        <AdaptiveTable<AgentCargoItemDTO>
          size="small"
          pagination={false}
          scroll={{ y: 360 }}
          rowKey={(r) => String(r.sourceIndex ?? r.cartonId)}
          dataSource={items}
          locale={{ emptyText: '暂无代理箱数据' }}
          onRow={(r) => ({ style: r.existingCartonId ? { color: '#999', background: '#fafafa' } : {} })}
          columns={[
            { title: '箱号', dataIndex: 'cartonId', width: 170, ellipsis: true },
            {
              title: '长×宽×高(cm)',
              width: 130,
              render: (_: unknown, r) =>
                [r.lengthCm, r.widthCm, r.heightCm].every((v) => v != null)
                  ? `${r.lengthCm}×${r.widthCm}×${r.heightCm}`
                  : '-',
            },
            { title: '毛重(kg)', dataIndex: 'measuredWeightKg', width: 90, render: (v?: number) => (v != null ? v : '-') },
            { title: '计费重(kg)', dataIndex: 'billingWeightKg', width: 100, render: (v?: number) => (v != null ? v : '-') },
            {
              title: '归属批次',
              width: 240,
              render: (_: unknown, r, i) => {
                const k = keyOf(r, i);
                if (r.existingCartonId) {
                  return (
                    <Select
                      size="small"
                      style={{ width: '100%' }}
                      value={assign[k] ?? r.existingBatchId ?? NONE}
                      options={batchOptions}
                      onChange={(v) => setAssign((prev) => ({ ...prev, [k]: v }))}
                      placeholder="已生成"
                    />
                  );
                }
                return (
                  <Select
                    size="small"
                    style={{ width: '100%' }}
                    value={assign[k] ?? NONE}
                    options={batchOptions}
                    onChange={(v) => setAssign((prev) => ({ ...prev, [k]: v }))}
                  />
                );
              },
            },
            {
              title: '状态',
              width: 80,
              render: (_: unknown, r) => (r.existingCartonId ? <Tag>已生成</Tag> : <Tag color="blue">新</Tag>),
            },
          ]}
        />
        <Checkbox checked={updateCount} onChange={(e) => setUpdateCount(e.target.checked)} style={{ marginTop: 12 }}>
          按实际匹配数更新批次件数
        </Checkbox>
      </Spin>
    </Modal>
  );
};

export default AgentCargoBatchMatchModal;
